import { useState, useCallback, useEffect } from 'react';
import { SPSyncGrantInfo, SPSyncResult, getGrantList, reconcile } from '../../../api/v2/SPSyncApi';
import { useAppContext } from '../../shared/AppContext';
import { useFilteredRows } from './useFilteredRows';

const STORAGE_KEY = 'spSyncState';

interface PersistedState {
    selectedFY: number;
    grantList: SPSyncGrantInfo[];
    selectedGrants: string[];
    result: SPSyncResult | null;
    showOnlyDiffs: boolean;
}

const loadPersistedState = (): PersistedState | null => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const savePersistedState = (state: PersistedState) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch { /* ignore quota errors */ }
};

export const useSPSyncController = () => {
    const app = useAppContext();
    const persisted = loadPersistedState();

    const [selectedFY, setSelectedFY] = useState<number>(persisted?.selectedFY ?? new Date().getFullYear());
    const [grantList, setGrantList] = useState<SPSyncGrantInfo[]>(persisted?.grantList ?? []);
    const [selectedGrants, setSelectedGrants] = useState<string[]>(persisted?.selectedGrants ?? []);
    const [result, setResult] = useState<SPSyncResult | null>(persisted?.result ?? null);
    const [loadingGrants, setLoadingGrants] = useState(false);
    const [loadingReconcile, setLoadingReconcile] = useState(false);
    const [showOnlyDiffs, setShowOnlyDiffs] = useState(persisted?.showOnlyDiffs ?? false);

    // Persist state changes so navigating back restores them
    useEffect(() => {
        savePersistedState({ selectedFY, grantList, selectedGrants, result, showOnlyDiffs });
    }, [selectedFY, grantList, selectedGrants, result, showOnlyDiffs]);

    const loadGrants = useCallback(async () => {
        setLoadingGrants(true);
        setResult(null);
        setSelectedGrants([]);
        try {
            const res = await getGrantList(selectedFY);
            if (res?.error) {
                app.errorToast(res.error.message);
                return;
            }
            setGrantList(res?.data ?? []);
        } finally {
            setLoadingGrants(false);
        }
    }, [selectedFY]);

    const runReconcile = useCallback(async () => {
        if (selectedGrants.length === 0) {
            app.infoToast("Select at least one grant to reconcile.");
            return;
        }
        setResult(null);
        setLoadingReconcile(true);
        try {
            const res = await reconcile(selectedFY, selectedGrants);
            if (res?.error) {
                app.errorToast(res.error.message);
                return;
            }
            setResult(res?.data ?? null);
        } finally {
            setLoadingReconcile(false);
        }
    }, [selectedFY, selectedGrants]);

    const filteredRows = useFilteredRows(result?.rows, showOnlyDiffs);

    const selectAllGrants = useCallback(() => {
        setSelectedGrants(grantList.map(g => g.baseGrantNumber));
    }, [grantList]);

    const clearGrantSelection = useCallback(() => {
        setSelectedGrants([]);
    }, []);

    return {
        isSuper: app.UserIsSuper,
        selectedFY,
        setSelectedFY,
        grantList,
        selectedGrants,
        setSelectedGrants,
        result,
        loadingGrants,
        loadingReconcile,
        showOnlyDiffs,
        setShowOnlyDiffs,
        loadGrants,
        runReconcile,
        filteredRows,
        selectAllGrants,
        clearGrantSelection
    };
};
